import { NOVITA_UNREAD_WINDOW_DAYS } from './config';

export const emptyNovitaUnreadState = () => ({
  unreadCount: 0,
  hasUnread: false,
  latestItemDate: null,
  latestItemSlug: null,
  latestItemId: null,
  previewItem: null,
  unreadItemIds: [],
  eligibleItemIds: [],
  totalItems: 0,
});

export const toNovitaTimestamp = (value) => {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

export const parseSessionDate = (value) => {
  if (typeof value === 'string' && value.trim()) return value;
  if (value instanceof Date && Number.isFinite(value.getTime())) {
    return value.toISOString();
  }
  if (value && typeof value === 'object' && typeof value.iso === 'string') {
    return value.iso;
  }
  return null;
};

export const getLatestNovitaItem = (items) =>
  [...items].sort(
    (left, right) =>
      (toNovitaTimestamp(right.publishedAt) || 0) -
      (toNovitaTimestamp(left.publishedAt) || 0)
  )[0] || null;

export const isNovitaItemEligibleForUnread = (
  item,
  userCreatedAt,
  now = new Date()
) => {
  const publishedAt = toNovitaTimestamp(item?.publishedAt);
  const userCreated = toNovitaTimestamp(userCreatedAt);
  const nowTimestamp = now.getTime();

  if (
    publishedAt === null ||
    !Number.isFinite(nowTimestamp) ||
    publishedAt > nowTimestamp
  ) {
    return false;
  }

  if (userCreated === null || publishedAt <= userCreated) {
    return false;
  }

  const windowStart =
    nowTimestamp - NOVITA_UNREAD_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  return publishedAt >= windowStart;
};

export const isNovitaItemUnread = (
  item,
  readItemIds,
  userCreatedAt,
  now = new Date()
) =>
  isNovitaItemEligibleForUnread(item, userCreatedAt, now) &&
  !readItemIds.has(item.id);

const toNovitaUnreadPreviewItem = (item) => {
  if (!item) return null;

  return {
    id: item.id,
    slug: item.slug,
    title: item.title,
    publishedAt: item.publishedAt,
    label: item.label,
    cover: item.cover,
    url: item.url,
    embedUrl: item.embedUrl,
  };
};

export const buildNovitaUnreadState = ({
  items,
  totalItems,
  readItemIds = [],
  userCreatedAt,
  now = new Date(),
}) => {
  const readIds = new Set(readItemIds);
  const latestItem = getLatestNovitaItem(items);
  const eligibleItemIds = items
    .filter((item) => isNovitaItemEligibleForUnread(item, userCreatedAt, now))
    .map((item) => item.id);
  const unreadItemIds = eligibleItemIds.filter(
    (itemId) => !readIds.has(itemId)
  );
  const latestUnreadItem = getLatestNovitaItem(
    items.filter((item) => unreadItemIds.includes(item.id))
  );

  return {
    unreadCount: unreadItemIds.length,
    hasUnread: unreadItemIds.length > 0,
    latestItemDate: latestItem?.publishedAt || null,
    latestItemSlug: latestItem?.slug || null,
    latestItemId: latestItem?.id || null,
    previewItem: toNovitaUnreadPreviewItem(latestUnreadItem),
    unreadItemIds,
    eligibleItemIds,
    totalItems,
  };
};

export const formatNovitaUnreadCount = (count) =>
  count > 99 ? '99+' : String(count);

export const buildNovitaUnreadSummaryLabel = (count) =>
  count === 1 ? 'novità non letta' : 'novità non lette';
